import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Product image gallery in One Piece manga aesthetic.
 * - Large main image with framer-motion crossfade/slide
 * - Thumbnails row (bottom on mobile, side on lg+) — only shown when >1 image
 * - Prev / Next arrows — only shown when >1 image
 * - Swipe (drag) support on touch devices via framer-motion
 * - Keyboard arrows on desktop
 * - No controls / thumbnails when there is a single image
 */
export default function ProductImageGallery({ images = [], productTitle = "" }) {
    // Normalize + fallback
    const list =
        images && images.length > 0
            ? images
            : [{ url: "", alt: productTitle }];
    const total = list.length;
    const hasMany = total > 1;

    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(0);

    // Reset when the product changes
    useEffect(() => {
        setIndex(0);
        setDirection(0);
    }, [images]);

    const goTo = useCallback(
        (nextIndex) => {
            if (!hasMany) return;
            const wrapped = ((nextIndex % total) + total) % total;
            setDirection(wrapped > index ? 1 : -1);
            setIndex(wrapped);
        },
        [hasMany, total, index]
    );

    const next = useCallback(() => goTo(index + 1), [goTo, index]);
    const prev = useCallback(() => goTo(index - 1), [goTo, index]);

    // Keyboard navigation
    useEffect(() => {
        if (!hasMany) return;
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [hasMany, next, prev]);

    const current = list[index];

    return (
        <div
            className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-5"
            data-testid="product-gallery"
        >
            {/* Thumbnails — vertical on lg+, horizontal below main on mobile */}
            {hasMany && (
                <div
                    className="order-2 lg:order-1 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto lg:max-h-[560px] pr-1 lg:pr-0 -mx-1 lg:mx-0 px-1 lg:px-0 scrollbar-thin"
                    role="tablist"
                    aria-label="Miniature del prodotto"
                >
                    {list.map((img, i) => {
                        const active = i === index;
                        return (
                            <button
                                key={img.url + i}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => goTo(i)}
                                data-testid={`product-thumb-${i}`}
                                className={`shrink-0 w-16 h-16 lg:w-20 lg:h-20 border-2 border-ink relative overflow-hidden transition-manga bg-seafoam ${
                                    active
                                        ? "ring-4 ring-bounty shadow-manga"
                                        : "opacity-70 hover:opacity-100"
                                }`}
                            >
                                <div className="absolute inset-0 halftone opacity-20" />
                                <img
                                    src={img.url}
                                    alt={img.alt || `${productTitle} — ${i + 1}`}
                                    className="relative w-full h-full object-cover"
                                    loading="lazy"
                                />
                                {active && (
                                    <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-bounty text-parchment font-display text-[10px] flex items-center justify-center leading-none border border-ink">
                                        {i + 1}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Main image */}
            <div className="order-1 lg:order-2 relative flex-1 min-w-0">
                <div className="bounty-card overflow-hidden">
                    <div className="aspect-[4/5] bg-seafoam relative overflow-hidden select-none">
                        <div className="absolute inset-0 halftone opacity-20 pointer-events-none" />

                        <AnimatePresence
                            initial={false}
                            custom={direction}
                            mode="wait"
                        >
                            <motion.img
                                key={current.url + index}
                                src={current.url}
                                alt={current.alt || productTitle}
                                custom={direction}
                                initial={{
                                    x: direction > 0 ? 60 : -60,
                                    opacity: 0,
                                }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{
                                    x: direction > 0 ? -60 : 60,
                                    opacity: 0,
                                }}
                                transition={{
                                    duration: 0.28,
                                    ease: "easeOut",
                                }}
                                drag={hasMany ? "x" : false}
                                dragConstraints={{ left: 0, right: 0 }}
                                dragElastic={0.25}
                                onDragEnd={(_, info) => {
                                    if (!hasMany) return;
                                    if (info.offset.x < -80 || info.velocity.x < -400) next();
                                    else if (info.offset.x > 80 || info.velocity.x > 400) prev();
                                }}
                                className="relative w-full h-full object-cover cursor-grab active:cursor-grabbing"
                                draggable={false}
                                data-testid="product-main-image"
                            />
                        </AnimatePresence>

                        {hasMany && (
                            <>
                                <button
                                    type="button"
                                    onClick={prev}
                                    aria-label="Immagine precedente"
                                    data-testid="product-gallery-prev"
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-ink text-parchment border-2 border-parchment hover:bg-bounty transition-manga shadow-manga"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={next}
                                    aria-label="Immagine successiva"
                                    data-testid="product-gallery-next"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-ink text-parchment border-2 border-parchment hover:bg-bounty transition-manga shadow-manga"
                                >
                                    <ChevronRight size={18} />
                                </button>

                                {/* Counter chip */}
                                <div
                                    className="absolute bottom-3 right-3 px-2 py-1 bg-ink text-parchment font-mono text-xs tracking-widest border-2 border-parchment"
                                    data-testid="product-gallery-counter"
                                >
                                    {index + 1} / {total}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="tape -top-3 left-10" />
                <div className="tape -top-3 right-10 rotate-[6deg]" />
            </div>
        </div>
    );
}
